import { Component, EventEmitter, Injector, Input, OnInit, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { SubscriptSizing } from '@angular/material/form-field';
import { AnyType } from '@thehubz/shared/model';

@Component({
  selector: 'sp-date-picker',
  templateUrl: './date-picker.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => DatePickerComponent),
    },
  ],
})
export class DatePickerComponent implements OnInit, ControlValueAccessor {
  /** CSS class */
  @Input() dateCssClass!: string;
  /** Disable date */
  @Input() isDisabled = false;
  /** Enable clear button */
  @Input() enableClearButton = false;
  /** Field label */
  @Input() fieldLabel?: string;
  /** Behaviors for how the subscript height is set. */
  @Input() subscriptSizing: SubscriptSizing = 'fixed';

  @Input() placeholder?: string;
  /** Emit date change event */
  @Output() selectedDate = new EventEmitter<MatDatepickerInputEvent<Date, AnyType>>();

  select = new FormControl();

  ngControl!: NgControl;

  constructor(private inj: Injector) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  onChange!: (value: Date | string | null) => void;
  onTouched!: () => void;

  ngOnInit(): void {
    this.ngControl = this.inj.get(NgControl);
  }

  // ========================= ControlValueAccessor interface methods =========================
  writeValue(value: Date): void {
    if (value !== undefined) {
      this.select.patchValue(value);
    }
    // copy parent form validation and assign it to child formControl
    const validators = this.ngControl.control?.validator;
    this.select.setValidators(validators ? validators : null);
    this.select.updateValueAndValidity();
  }

  registerOnChange(fn: AnyType): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: AnyType): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) this.select?.disable();
    else this.select?.enable();
    this.isDisabled = isDisabled;
  }
  // ==========================================================================================

  /**
   * Clear date
   */
  clearDate() {
    this.onChange('');
    this.onTouched();
    this.select.patchValue('');
  }

  onDateSelection(evt: MatDatepickerInputEvent<Date, AnyType>) {
    this.onChange(evt.value);
    this.onTouched();
    this.selectedDate.emit(evt);
    // copy parent form validation and assign it to child formControl
    const validators = this.ngControl.control?.validator;
    this.select.setValidators(validators ? validators : null);
    this.select.updateValueAndValidity();
  }

  onInput(event: AnyType) {
    const input = event.target.value;
    // Check if input is in numeric form (e.g., 24082024)
    if (/^\d{8}$/.test(input)) {
      const day = input.substring(0, 2);
      const month = input.substring(2, 4);
      const year = input.substring(4, 8);

      // Format the date as DD/MM/YYYY
      const formattedDate = `${month}/${day}/${year}`;

      // Update the dateValue bound to the input
      const newDate = new Date(formattedDate);
      this.select.patchValue(newDate);
    }
  }
}
